import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import React from "react";
import SaveIcon from "@mui/icons-material/Save";
import { setDisplayErrorSnackBar } from "../../../../../../../../../features/userSlice";
import CommonDataGridTable from "../../../../../CommonDataGridTable/CommonDataGridTable";
import { useDispatch } from "react-redux";
import { useSearchTermsTab } from "../../../../../STOperationhooks/useSearchTermsTab";
import {
  setConfirmLoaderSTControl,
  setSelectedDefinitionsForCapturedByMultipleKeywords,
  setSelectedRowsForCapturedByMultipleKeywords,
} from "../../../../../../../../../features/searchTermTabSlice";
import { dispatchSnackBarMessages } from "../../../../../utilsSearchTerm/dispatchSnackBarMessages";

interface DefinitionModalSTProps {
  open: boolean;
  onClose: () => void;
}

const DefinitionModalForCaptureByKeywords: React.FC<DefinitionModalSTProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const {
    selectedRowsForCapturedByMultipleKeywords, // get the selected rows
    selectedDefinitionsForCapturedByMultipleKeywords, // get the selected definitions with rows
    definitionModalSourceForCapturedByMultipleKeywords, // get the source of modal open (headerIcon or saveButton)
    definitionModalTypeForCapturedByMultipleKeywords, // get the modal type (Exclusive, Allowed, Negative)
    confirmLoaderSTControl,
  } = useSelector((state: any) => state.searchTermTab);

  const {
    createPositiveSearchTermLinksMulti,
    createNegativeSearchTermLinks,
    getCapturedByMultipleKeywords,
    activateOrdeactivateExistingLinks,
  } = useSearchTermsTab(); //custom hook to handle API calls

  // === COLUMNS ===
  const columns: GridColDef[] = [
    {
      field: "keyword_name",
      headerName: "Keyword Name",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "search_term_match_type",
      headerName: "Match Type",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "adgroup_name",
      headerName: "Adgroup Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "campaign_name",
      headerName: "Campaign Name",
      flex: 1,
      minWidth: 220,
    },
  ];

  let mergedList = [];

  // === MERGE SELECTED ROWS WITH DEFINITIONS ===
  if (definitionModalSourceForCapturedByMultipleKeywords === "headerIcon") {
    mergedList = selectedRowsForCapturedByMultipleKeywords.map((row: any) => ({
      id: row.id,
      search_term: row.search_term,
      search_term_shared_uid: row.search_term_shared_uid,
      keyword_name: row.keyword_name,
      keyword_id: row.keyword_id,
      search_term_match_type: row.search_term_match_type,
      campaign_id: row.campaign_id,
      campaign_name: row.campaign_name,
      adgroup_id: row.adgroup_id,
      adgroup_name: row.adgroup_name,
      ad_shared_uid: row.ad_shared_uid,
      scope_shared_uid: row.scope_shared_uid,
      action: definitionModalTypeForCapturedByMultipleKeywords || "", // fallback for safety
    }));
  } else if (
    definitionModalSourceForCapturedByMultipleKeywords === "saveButton"
  ) {
    mergedList = Object.values(selectedDefinitionsForCapturedByMultipleKeywords)
      .filter((row: any) => {
        // check if action exists and is not empty
        return (
          row?.action &&
          typeof row.action === "string" &&
          row.action.trim() !== ""
        );
      })
      .map((row: any) => ({
        id: row.id,
        search_term: row.search_term,
        search_term_shared_uid: row.search_term_shared_uid,
        keyword_name: row.keyword_name,
        keyword_id: row.keyword_id,
        search_term_match_type: row.search_term_match_type,
        campaign_id: row.campaign_id,
        campaign_name: row.campaign_name,
        adgroup_id: row.adgroup_id,
        adgroup_name: row.adgroup_name,
        ad_shared_uid: row.ad_shared_uid,
        scope_shared_uid: row.scope_shared_uid,
        action: row.action, // action is already part of the row object
      }));
  } else {
    mergedList = [];
  }
  // === GROUP DATA BY ACTION ===
  const groupedData = {
    Exclusive: mergedList.filter((item: any) => item.action === "Exclusive"),
    Negative: mergedList.filter((item: any) => item.action === "Negative"),
  };
  const renderGrid = (title: string, rows: any[]) => {
    if (rows.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography>{title}</Typography>
        <CommonDataGridTable rows={rows} columns={columns} />
      </Box>
    );
  };

  const hasData =
    groupedData.Exclusive.length > 0 || groupedData.Negative.length > 0;

  // === Prepare negative flow ===
  const prepareNegativeFlow = async (negativeData: any) => {
    if (negativeData.length === 0) return null;

    // Step 1: Deactivate existing positive links
    const deactivateSearchTermUids = negativeData.map(
      (item: any) => item.search_term_shared_uid,
    );
    const requestBodyForDeactivation = {
      terms: deactivateSearchTermUids.map((uid: string) => ({
        shared_uid: uid,
        target_is_active: false,
        add_negative_to_other_ad_groups: false,
      })),
    };
    const { successCount: deactivateSuccess, successfulUids: deactivatedUids } =
      await activateOrdeactivateExistingLinks(requestBodyForDeactivation);

    if (deactivateSuccess === 0 || deactivatedUids.length === 0) {
      return {
        successCount: 0,
        failCount: negativeData.length,
        failedSearchTermUids: [],
        messages: [
          {
            message: "Failed to deactivate any search terms",
            type: "error",
          },
        ],
      };
    }

    // Step 2: Create negative links for successfully deactivated terms
    const deactivatedUidsSet = new Set(deactivatedUids);
    const successfullyDeactivatedItems = negativeData.filter((item: any) =>
      deactivatedUidsSet.has(item.search_term_shared_uid),
    );
    const reqBody = {
      terms: successfullyDeactivatedItems.map((item: any) => ({
        term: item.search_term,
        match_type: item.search_term_match_type,
        level: "ad_group",
        level_shared_uid: item.ad_shared_uid,
        additional_info: {
          search_term_shared_uid: item.search_term_shared_uid,
        },
      })),
    };

    return await createNegativeSearchTermLinks(reqBody);
  };

  // === CLASSIFY SEARCH TERMS(Allowed => Exclusive / Negative) ===
  const classifySearchTermsHandler = async () => {
    dispatch(setConfirmLoaderSTControl(true));
    let totalSuccess = 0;
    let failedSharedUids: string[] = [];
    // let totalFailed = 0;
    let allMessages: { message: string; type: string }[] = []; // ✅ outer scope

    try {
      const positiveTerms = [
        ...groupedData.Exclusive.map((item: any) => ({
          search_term: item.search_term,
          search_term_match_type: item.search_term_match_type,
          keyword_id: item.keyword_id,
          scope_shared_uid: item.scope_shared_uid,
          link_type: "Exclusive",
          add_negative_to_other_ad_groups: true,
        })),
      ];
      const negativeData = groupedData.Negative || [];

      // === Run positive and negative flows in parallel ===
      const [positiveResult, negativeResult] = await Promise.all([
        positiveTerms.length > 0
          ? createPositiveSearchTermLinksMulti({ terms: positiveTerms })
          : null,
        negativeData.length > 0 ? prepareNegativeFlow(negativeData) : null,
      ]);

      // === Collect results ===
      totalSuccess +=
        (positiveResult?.successCount ?? 0) +
        (negativeResult?.successCount ?? 0);
      failedSharedUids = negativeResult?.failedSearchTermUids ?? [];

      // === Combine Display messages ===
      allMessages = [
        ...(positiveResult?.messages ?? []),
        ...(negativeResult?.messages ?? []),
      ];
      // Display messages if there are any
      if (allMessages.length > 0) {
        dispatchSnackBarMessages(allMessages, dispatch);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to classify search terms.";
      dispatch(
        setDisplayErrorSnackBar({
          message: `Critical error during classification: ${errorMessage}`,
          type: "error",
        }),
      );
    } finally {
      dispatch(setConfirmLoaderSTControl(false));
      //Step 3: Reactivate search terms that failed to be added as negative
      if (failedSharedUids && failedSharedUids.length > 0) {
        const requestBodyForActivation = {
          terms: failedSharedUids.map((uid: string) => ({
            shared_uid: uid,
            target_is_active: true, // Set to true for activation
            add_negative_to_other_ad_groups: false, // Set to false to as converted positive(Allowed) to negative links
          })),
        };
        await activateOrdeactivateExistingLinks(requestBodyForActivation);
      }

      // Refresh data + close Modal + claer redux if anything succeeded
      if (totalSuccess > 0) {
        dispatch(setSelectedRowsForCapturedByMultipleKeywords([])); // Clear selected rows
        dispatch(setSelectedDefinitionsForCapturedByMultipleKeywords({}));
        onClose(); // Close modal

        await getCapturedByMultipleKeywords();
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent close if loading and the reason is backdrop click or escape key
        if (
          // confirmLoaderUnclassifiedST &&
          reason === "backdropClick" ||
          reason === "escapeKeyDown"
        ) {
          return;
        }
        onClose();
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Total Number of Adgroups effected: {mergedList.length}
      </DialogTitle>
      <DialogContent>
        {hasData ? (
          <>
            {renderGrid("Set as Exclusive", groupedData.Exclusive)}
            {renderGrid("Set as Negative", groupedData.Negative)}
          </>
        ) : (
          <Typography
            sx={{ py: 4, textAlign: "center", color: "text.secondary" }}
          >
            No search terms selected.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "16px 20px" }}>
        <Button
          onClick={onClose}
          disabled={confirmLoaderSTControl}
          color="secondary"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          data-tracker="577"
          variant="contained"
          color="secondary"
          loading={confirmLoaderSTControl}
          loadingPosition="end"
          endIcon={confirmLoaderSTControl && <SaveIcon />}
          disabled={!hasData}
          onClick={classifySearchTermsHandler}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DefinitionModalForCaptureByKeywords;
